import { ApolloClient } from 'apollo-client'
import { ApolloLink } from 'apollo-link'
import { HttpLink } from 'apollo-link-http'
import { InMemoryCache } from 'apollo-cache-inmemory'
import Vue from 'vue'
import VueApollo from 'vue-apollo'
import fetch from './utils/register-fetch'
import { ApolloLink as ApolloLinkBase, Observable } from 'apollo-link'
import { waitingRoomHeaders } from './utils/waiting-room-session'

Vue.use(VueApollo)

const uri = `${process.env.VUE_APP_SERVER_URL}/graphql`

const CACHE_TTL = 10 * 60 * 1000 // 10 minutes in milliseconds

// Sales data must always come from the API, including previously cached zeroes.
// Event details contain capacity, dates and ticketing settings, so they are live too.
const queriesToCache = [
  'getLanguageCodesForEvent',
]

const DISPLAY_CACHE_PREFIX = 'register-display-v1:'
const stableJSON = value => JSON.stringify(value, (key, item) => {
  if (key === 'loc') return undefined
  if (item && !Array.isArray(item) && typeof item === 'object') {
    return Object.keys(item).sort().reduce((result, name) => {
      result[name] = item[name]

      return result
    }, {})
  }

  return item
})

// Only this public DTO may cross reloads. A fresh state response supplies the
// revision and clock; device clock changes cannot extend its server expiry.
function validDisplay(data, variables, serverNow) {
  const display = data && data.getEventRegisterDisplay
  const allowed = ['id', 'slug', 'title', 'description', 'cover', 'coverImagePrimaryColor', 'location', 'revision', 'generatedAt', 'expiresAt', '__typename']

  const completeObject = (value, keys) => value === null || (value &&
    keys.every(key => Object.prototype.hasOwnProperty.call(value, key)) &&
    Object.keys(value).every(key => key === '__typename' || keys.includes(key)))

  return Number.isFinite(serverNow) && display &&
    completeObject(display.coverImagePrimaryColor, ['r', 'g', 'b']) &&
    completeObject(display.location, ['id', 'address1', 'address2', 'city', 'postalCode', 'state', 'country', 'venue', 'latitude', 'longitude']) &&
    Object.keys(data).every(key => key === 'getEventRegisterDisplay') &&
    Object.keys(display).every(key => allowed.includes(key)) &&
    allowed.filter(key => key !== '__typename').every(key => Object.prototype.hasOwnProperty.call(display, key)) &&
    display.slug === variables.slug && typeof display.title === 'string' &&
    typeof variables.expectedRevision === 'string' && display.revision === variables.expectedRevision &&
    Number.isFinite(Date.parse(display.generatedAt)) && Date.parse(display.generatedAt) <= serverNow &&
    Date.parse(display.expiresAt) > serverNow
}

const cacheLink = new ApolloLinkBase((operation, forward) => {
  const { operationName, variables = {}, query } = operation
  // Fail closed for combined/fragment queries: a static operation name must not
  // make a response containing inventory cacheable.
  const definition = query.definitions.length === 1 && query.definitions[0]
  const selections = definition && definition.selectionSet && definition.selectionSet.selections
  const cacheable = queriesToCache.includes(operationName) &&
    definition.kind === 'OperationDefinition' && definition.operation === 'query' &&
    selections.length === 1 && selections[0].kind === 'Field' &&
    selections[0].name.value === operationName && !selections[0].alias

  if (operationName === 'getEventRegisterDisplay') {
    const displayOnly = definition && definition.kind === 'OperationDefinition' &&
      definition.operation === 'query' && selections.length === 1 &&
      selections[0].kind === 'Field' && !selections[0].alias &&
      selections[0].name.value === 'getEventRegisterDisplay'
    const context = operation.getContext()
    const serverNow = context.registerServerNow ? Date.parse(context.registerServerNow) : NaN
    if (!displayOnly || !Number.isFinite(serverNow) || !variables.expectedRevision) return forward(operation)
    const key = DISPLAY_CACHE_PREFIX + stableJSON({ query, variables })
    try {
      const raw = window.localStorage.getItem(key)
      if (raw) {
        const cached = JSON.parse(raw)
        if (validDisplay(cached, variables, serverNow)) return Observable.of({ data: cached })
        window.localStorage.removeItem(key)
      }
    } catch (error) { /* Optional storage; use the network. */ }

    return forward(operation).map(response => {
      // The display may have been generated just after the state snapshot.
      const receivedNow = Math.max(serverNow, Date.parse(response.data && response.data.getEventRegisterDisplay && response.data.getEventRegisterDisplay.generatedAt))
      if (!response.errors && validDisplay(response.data, variables, receivedNow)) {
        try {
          const storage = window.localStorage
          const keys = []
          for (let index = 0; index < storage.length; index++) {
            const existing = storage.key(index)
            if (existing && existing.startsWith(DISPLAY_CACHE_PREFIX)) keys.push(existing)
          }
          // Bound our namespace without touching saved orders or unrelated data.
          while (keys.length >= 24) storage.removeItem(keys.shift())
          storage.setItem(key, JSON.stringify(response.data))
        } catch (error) { /* Quota/private mode never blocks checkout. */ }
      }

      return response
    })
  }

  let slug = variables.slug
  try {
    slug = slug || window.localStorage.getItem(`redirect:${variables.eventId}`)
    if (slug) {
      const key = `${operationName}:${slug}`
      if (!cacheable) {
        // Remove old availability entries when encountered after an upgrade.
        window.localStorage.removeItem(key)
      } else {
        const cachedRaw = window.localStorage.getItem(key)
        if (cachedRaw) {
          const { timestamp, data } = JSON.parse(cachedRaw)
          if (Date.now() - timestamp < CACHE_TTL) {
            return Observable.of({ data })
          }
          window.localStorage.removeItem(key)
        }
      }
    }
  } catch (error) {
    // Unavailable browser storage must never prevent a fresh availability read.
  }

  if (!cacheable) return forward(operation)

  return forward(operation).map(response => {
    if (!response.errors && response.data) {
      const event = response.data.getEventAdditionalDataForRegisterContainer
      const canonicalSlug = (event && event.slug) || slug
      if (canonicalSlug) {
        try {
          window.localStorage.setItem(
            `${operationName}:${canonicalSlug}`,
            JSON.stringify({ timestamp: Date.now(), data: response.data }),
          )
        } catch (error) {
          // Caching is optional when browser storage is unavailable or full.
        }
      }
    }

    return response
  })
})

const httpLink = new HttpLink({ uri, fetch, credentials: 'include' })

const admissionLink = new ApolloLinkBase((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({ headers: { ...headers, ...waitingRoomHeaders() } }))

  return forward(operation)
})
const link = ApolloLink.from([admissionLink, cacheLink, httpLink])

const cache = new InMemoryCache({
  addTypename: true,
})

// persistCache({
//   cache,
//   storage: window.localStorage,
// })

export const apolloClient = new ApolloClient({
  link,
  cache,
  connectToDevTools: true,
  defaultOptions: {
    watchQuery: { fetchPolicy: 'no-cache', errorPolicy: 'ignore' },
    query: { fetchPolicy: 'no-cache', errorPolicy: 'all' },
    mutate: {
      errorPolicy: 'all',
    },
  },
})

export default new VueApollo({
  defaultClient: apolloClient,
})
